// Board-sized shader passes (the fog of war, the weather's ground, the liquids' surface) render to a
// texture one fragment per art pixel. A crawl for twenty players is ~2500×1650 art px: four million
// fragments of noise twenty times a second, nearly all of them off screen. Each pass now shades only
// the part of its texture the camera shows, and a margin round it (a scissor box: the rest of the
// texture keeps what it last held), and renders again at once — not at its next 20 Hz tick — when
// the camera's view leaves the part last rendered. Every pixel on screen is what the full pass drew:
// the shader is a function of the pixel, its uniforms and its masks, all unchanged.
import Phaser from 'phaser';

/** A rectangle in board px. */
export interface PxRect { x: number; y: number; w: number; h: number }

/** The margin rendered round the view, as a share of the view's size on each side. */
export const VIEW_MARGIN = 0.25;

/**
 * What `cam` shows of a `W`×`H` board-px area (board px, whole pixels, clamped to it), widened by
 * `margin` of the view's own size on each side. The camera's own transform (`Camera.preRender`):
 * screen p = origin + zoom·(world − scroll − origin). Read off its scroll and zoom as they stand,
 * so a view moved earlier in this frame counts.
 */
export function viewRect(cam: Pick<Phaser.Cameras.Scene2D.Camera, 'width' | 'height' | 'originX' | 'originY' | 'scrollX' | 'scrollY' | 'zoomX' | 'zoomY'>, W: number, H: number, margin = 0): PxRect {
  const ox = cam.width * cam.originX, oy = cam.height * cam.originY;
  const x0 = cam.scrollX + ox - ox / cam.zoomX, y0 = cam.scrollY + oy - oy / cam.zoomY;
  const vw = cam.width / cam.zoomX, vh = cam.height / cam.zoomY;
  const mx = vw * margin, my = vh * margin;
  const x = Math.max(0, Math.floor(x0 - mx)), y = Math.max(0, Math.floor(y0 - my));
  const x1 = Math.min(W, Math.ceil(x0 + vw + mx)), y1 = Math.min(H, Math.ceil(y0 + vh + my));
  return { x, y, w: Math.max(0, x1 - x), h: Math.max(0, y1 - y) };
}

/** Whether `a` lies wholly inside `b` (an empty `a` always does). */
export function within(a: PxRect, b: PxRect | undefined): boolean {
  if (a.w <= 0 || a.h <= 0) return true;
  return !!b && a.x >= b.x && a.y >= b.y && a.x + a.w <= b.x + b.w && a.y + a.h <= b.y + b.h;
}

/** The overlap of two rectangles (empty when they miss). */
export function overlap(a: PxRect, b: PxRect): PxRect {
  const x = Math.max(a.x, b.x), y = Math.max(a.y, b.y);
  return { x, y, w: Math.max(0, Math.min(a.x + a.w, b.x + b.w) - x), h: Math.max(0, Math.min(a.y + a.h, b.y + b.h) - y) };
}

/**
 * One board-sized render-to-texture shader, rendered only where the camera looks. `area` (board px)
 * is where the pass draws anything at all (the liquids' squares); outside it the texture is left
 * as the first full render made it.
 */
export class ViewRender {
  /** The part of the texture rendered last (what is fresh); none until the first render. */
  private fresh?: PxRect;
  private readonly onRestore = () => { this.fresh = undefined; };

  constructor(private readonly shader: Phaser.GameObjects.Shader, private readonly cam: Phaser.Cameras.Scene2D.Camera, private readonly W: number, private readonly H: number, private readonly area?: PxRect) {
    shader.scene.game.renderer.on(Phaser.Renderer.Events.RESTORE_WEBGL, this.onRestore);
    shader.once(Phaser.GameObjects.Events.DESTROY, () => shader.scene?.game.renderer.off(Phaser.Renderer.Events.RESTORE_WEBGL, this.onRestore));
  }

  /** The part to render now: the view and its margin (within `area`). */
  private target(): PxRect {
    const v = viewRect(this.cam, this.W, this.H, VIEW_MARGIN);
    return this.area ? overlap(v, this.area) : v;
  }

  /** The camera now shows something outside the part last rendered: render before this frame draws. */
  get needed(): boolean {
    const v = viewRect(this.cam, this.W, this.H);
    return !within(this.area ? overlap(v, this.area) : v, this.fresh);
  }

  /** Render the view's part (`full`: all of it, e.g. drawn once under reduce motion). */
  render(full = false): void {
    const dc = (this.shader as unknown as { drawingContext?: { setScissorEnable(on: boolean): void; setScissorBox(x: number, y: number, w: number, h: number): void } }).drawingContext;
    const all = { x: 0, y: 0, w: this.W, h: this.H };
    const r = full || !dc ? all : this.target();
    if (r.w <= 0 || r.h <= 0) { this.fresh = r; return; }
    if (dc) {
      const whole = r.x === 0 && r.y === 0 && r.w === this.W && r.h === this.H;
      dc.setScissorEnable(!whole);
      if (!whole) dc.setScissorBox(r.x, r.y, r.w, r.h);
    }
    this.shader.renderImmediate();
    this.fresh = r;
  }

  /** Forget what was rendered (the next `needed` is true): its masks or uniforms changed everywhere. */
  invalidate(): void { this.fresh = undefined; }
}
